import { DRAWING_LEVEL_LABEL_OFFSET_X_PX, DRAWING_FIBONACCI_BOLD_WIDTH_RATIO } from "./drawingConstants";
import { FIBONACCI_LEVELS, formatDrawingLevel } from "./drawingLevels";
import { mainStroke } from "./drawingShapeParts";

const BOLD_LEVELS = [0, 1];

// Level 0 lies on the second point, level 1 on the first one, the others follow from them
const levelY = (first, second, level) => second.y + (first.y - second.y) * level;

/**
 * Fibonacci Retracement (po-chart-visual-spec.md §7.5): a horizontal line per
 * level between the two points, levels 0 and 1 twice as thick, the points joined
 * by a dashed line; the name of a level on the left, its price on the right. See TOOLS in drawingTools.js.
 *
 * @param {Array<{ x: number, y: number }>} points - CSS px of the pane
 * @param {{ width: number, height: number }} pane
 * @param {object} style - FIBONACCI_RETRACEMENT_STYLE with colour and width
 * @returns {{ strokes: object[], fills: object[], labels: object[] }}
 */
export const fibonacciRetracementShape = ([first, second], pane, style) => {
  const left = Math.min(first.x, second.x);
  const right = Math.max(first.x, second.x);
  const levels = FIBONACCI_LEVELS.map((level) => ({ level, y: levelY(first, second, level) }));
  const toSegment = ({ y }) => [left, y, right, y];
  const isBold = ({ level }) => BOLD_LEVELS.includes(level);

  return {
    strokes: [
      { ...mainStroke(style, [[first.x, first.y, second.x, second.y]]), isDashed: true },
      mainStroke(style, levels.filter((level) => !isBold(level)).map(toSegment)),
      {
        ...mainStroke(style, levels.filter(isBold).map(toSegment)),
        width: style.width * DRAWING_FIBONACCI_BOLD_WIDTH_RATIO,
      },
    ],
    fills: [],
    labels: [
      style.isLevelsVisible && {
        color: style.levelsColor,
        align: "right",
        axis: "y",
        entries: levels.map(({ level, y }) => ({
          x: left - DRAWING_LEVEL_LABEL_OFFSET_X_PX,
          y,
          text: formatDrawingLevel(level, style.levelFormat),
        })),
      },
      style.isValuesVisible && {
        color: style.valuesColor,
        align: "left",
        axis: "y",
        entries: levels.map(({ y }) => ({ x: right + DRAWING_LEVEL_LABEL_OFFSET_X_PX, y, isPrice: true })),
      },
    ].filter(Boolean),
  };
};
